/**
 * Display formatting for UJP money / km / age. Pure, locale-fixed to id-ID so a
 * test and a screen agree byte-for-byte. Money arrives as decimal STRINGS from
 * the service — these helpers only render, they never compute totals.
 */

import type { MoneyString, NumberLike } from '@/services/api/ujp';

/**
 * `"695280.00"` | `"141.2"` | 695280 → number. Blank / null / garbage → 0.
 * Every decimal on the UJP wire is a string (folder contract), so read one
 * through this before comparing or rendering it.
 */
export const toNum = (value: NumberLike | null | undefined): number => {
  if (value == null || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** Money-flavoured alias of {@link toNum}. */
export const toMoneyNumber = (value: MoneyString | number | null | undefined): number =>
  toNum(value);

/** 695280 → `"Rp 695.280"`; null → `"—"`. */
export const formatRupiah = (
  value: MoneyString | number | null | undefined,
  { dash = true }: { dash?: boolean } = {}
): string => {
  if (dash && (value == null || value === '')) return '—';
  const n = Math.round(toMoneyNumber(value));
  return `Rp ${n.toLocaleString('id-ID')}`;
};

/** Digits-only string ("185000") → id-ID grouped display ("185.000"). */
export const groupDigits = (digits: string): string => {
  const clean = digits.replace(/\D/g, '');
  if (!clean) return '';
  return Number(clean).toLocaleString('id-ID');
};

/** 84.6 → `"84,6 km"`; up to 2 dp, trailing zeros trimmed. */
export const formatKm = (km: NumberLike | null | undefined, unit = true): string => {
  if (km == null || km === '') return '—';
  const n = toNum(km);
  if (!Number.isFinite(n)) return '—';
  const s = n.toLocaleString('id-ID', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return unit ? `${s} km` : s;
};

/** `"2026-09-20"` → `"20 Sep 2026"`; tolerant of ISO timestamps. */
export const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Whole days since `iso` (floored at 0). */
export const ageDays = (iso: string, now: Date = new Date()): number => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((now.getTime() - d.getTime()) / 86_400_000));
};

/** "baru saja" / "3 jam lalu" / "2 hari lalu". */
export const formatAge = (iso: string, now: Date = new Date()): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const mins = Math.max(0, Math.floor((now.getTime() - d.getTime()) / 60_000));
  if (mins < 1) return 'baru saja';
  if (mins < 60) return `${mins} mnt lalu`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} jam lalu`;
  return `${Math.floor(hours / 24)} hari lalu`;
};

/** `"****1234"` / `"•••• 1234"` → `"•••• 1234"`; keeps the last four digits. */
export const formatMaskedAccount = (masked: string): string => {
  const tail = masked.replace(/[^0-9]/g, '').slice(-4);
  return tail ? `•••• ${tail}` : '••••';
};

/** Today in the local calendar as `YYYY-MM-DD`. */
export const todayIso = (now: Date = new Date()): string => {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const toIsoDate = (d: Date): string => todayIso(d);

export const parseIsoDate = (s: string | undefined): Date | undefined => {
  if (!s) return undefined;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
};
